import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { localized, localizedInterpolation } from 'shared/state/i18n/i18n';
import { WebAppState } from 'web/state/store.web';
import { WhiteText } from 'web/view/components/Text/white-text';
import PageContainer from 'web/view/components/page-container/page-container';
import { SkioldButton } from 'web/view/components/skiold-components/skiold-button/skiold-button';
import { ViewWeb } from 'web/view/components/utils/web-view';
import { GetAnimalListPdf, GetDadMomTablePdf } from 'web/web-helpers/pdf-helper/animal-list-helper';
import { Heading } from '../../utils/heading';
import { BarChart } from '../../graph/bar-graph/bar-chart';
import { LineSeries, RawAxisProps, SeriesProps } from '@devexpress/dx-react-chart';
import moment from 'moment';
import { DashboardTableModelItem, PregnancyState } from 'shared/api/api';
import { Colors } from 'shared/assets/root-assets/colors';
import { LineChart } from '../../graph/line-graph/line-chart';
import { GetDashboardOverviewGraph } from 'shared/state/ducks/dashboard/operations';
import { ClipLoader } from 'react-spinners';

interface PropsFromParent {
	closeModal: () => void;
}
const getDashboardFarrowingItemsByWeek = (
	listObejct: { [key: string]: DashboardTableModelItem } | undefined,
	object: any,
	propKey: string,
) => {
	// get data for each dashboard item, and separate by week and propKey
	listObejct &&
		Object.keys(listObejct).forEach(key => {
			const val = listObejct[key];
			if (val.matingBatch?.farrowingPeriodStart) {
				if (!val.value) {
					return;
				}
				const date = moment(val.matingBatch.matingPeriodStart);

				const key = `${val.matingBatch.batchNumber} (${date.isoWeek()}-${date.year()})`;

				if (!object[key]) {
					object[key] = {};
				}
				object[key][propKey] = val.value ? +val.value.replace(',', '.') : 0;
				object[key].date = val.matingBatch?.farrowingPeriodStart;
			}
		});
};

export const TotalFarrowingLineGraph: FC<PropsFromParent> = React.memo(({ closeModal }) => {
	const dispatch = useDispatch();
	// Get dashboard data
	const dashboardFarrowingItems = useSelector((state: WebAppState) =>
		state.dashboardOverview.graphData &&
		state.dashboardOverview.graphData.dashboardTableData &&
		state.dashboardOverview.graphData.dashboardTableData['FarrowingDashboard']
			? state.dashboardOverview.graphData.dashboardTableData['FarrowingDashboard']
			: [],
	);
	const { graphLoading } = useSelector((state: WebAppState) => state.dashboardOverview);
	const { siteId } = useSelector((state: WebAppState) => state.profile.active!);

	useEffect(() => {
		if (siteId) {
			dispatch(GetDashboardOverviewGraph(siteId, true, PregnancyState.Farrowing));
		}
	}, [siteId]);

	// Set bar series
	const series = useMemo(() => {
		return [
			{
				argumentField: 'week',
				valueField: 'TotalFarrowings',
				name: 'TotalFarrowings',
				color: Colors.yellow,
				key: 'TotalFarrowings',
				scaleName: 'farrowings',
			} as SeriesProps,
			{
				argumentField: 'week',
				valueField: 'AvgAlive',
				name: 'AvgAlive',
				color: Colors.pastelBlue,
				key: 'AvgAlive',
				scaleName: 'pcs',
			} as SeriesProps,
			{
				argumentField: 'week',
				valueField: 'AvgLivebornFirstParity',
				name: 'AvgLivebornFirstParity',
				color: Colors.pastelGreen,
				key: 'AvgLivebornFirstParity',
				scaleName: 'pcs',
			} as SeriesProps,
			{
				argumentField: 'week',
				valueField: 'AvgDead',
				name: 'AvgDead',
				color: Colors.pastelRed,
				key: 'AvgDead',
				scaleName: 'pcs',
			} as SeriesProps,
			{
				argumentField: 'week',
				valueField: 'AvgDeadPiglets',
				name: 'AvgDeadPiglets',
				color: Colors.pastelOrange,
				key: 'AvgDeadPiglets',
				scaleName: 'pcs',
			} as SeriesProps,
			{
				argumentField: 'week',
				valueField: 'TotalBornAvg',
				name: 'TotalBornAvg',
				color: Colors.pastelOrange,
				key: 'TotalBornAvg',
				scaleName: 'pcs',
			} as SeriesProps,
		];
	}, []);

	//  set YAxis
	const yAxis = useMemo(() => {
		return [
			{ position: 'right', scaleName: 'farrowings', showGrid: false } as RawAxisProps,
			{ position: 'left', scaleName: 'pcs' } as RawAxisProps,
		];
	}, []);

	// generate bar chart data
	const data = useMemo(() => {
		// separate data by dashboard item
		// The is based on model IFarrowingReport
		let totalFarrowings = dashboardFarrowingItems.find(x => x.name === 'TotalFarrowings')?.data;
		let avgAlive = dashboardFarrowingItems.find(x => x.name === 'AvgAlive')?.data;
		let avgLivebornFirstParity = dashboardFarrowingItems.find(x => x.name === 'AvgLivebornFirstParity')?.data;
		let avgDead = dashboardFarrowingItems.find(x => x.name === 'AvgDead')?.data;
		let avgDeadPiglets = dashboardFarrowingItems.find(x => x.name === 'AvgDeadPiglets')?.data;

		// create object with week as key
		let objectData = {};

		// get data for each dashboard item
		getDashboardFarrowingItemsByWeek(totalFarrowings, objectData, 'TotalFarrowings');
		getDashboardFarrowingItemsByWeek(avgAlive, objectData, 'AvgAlive');
		getDashboardFarrowingItemsByWeek(avgLivebornFirstParity, objectData, 'AvgLivebornFirstParity');
		getDashboardFarrowingItemsByWeek(avgDead, objectData, 'AvgDead');
		getDashboardFarrowingItemsByWeek(avgDeadPiglets, objectData, 'AvgDeadPiglets');

		// map object to array
		let resultArray = Object.keys(objectData).map(key => {
			let week = objectData[key];
			if (week) {
				week.week = key;
				week.TotalBornAvg = Math.round((week.AvgAlive + week.AvgDead) * 10) / 10;
			}
			return week;
		});
		return resultArray.slice().sort((a, b) => a.date - b.date);
	}, [dashboardFarrowingItems]);

	return (
		<PageContainer>
			<Heading text={localized('Farrowings')} />
			<ViewWeb className="default-spacing">
				<ViewWeb className="default-close-pos">
					<SkioldButton theme="grey" onPress={closeModal} title={localized('Close')} />
				</ViewWeb>
				{graphLoading ? (
					<ViewWeb className="spinner-container-active-dashboard">
						<ClipLoader color="#f2ac40" size={70} />
					</ViewWeb>
				) : (
					<ViewWeb className="flex-row">
						<ViewWeb className="vertical-legend-title">
							<div className="rotate-container">
								<WhiteText className="header-white-text rotate-270">{localized('PigsPcs')}</WhiteText>
							</div>
						</ViewWeb>
						<ViewWeb className="flexOne">
							<LineChart
								lineSeries={series}
								chartData={data}
								valueAxis={yAxis}
								title=""
								yTitle={localized('FarrowingsPerBatchPcs')}
								keyTranslator={localized}
								zoomPan="both"
								verticalArgumentLabels={true}
							/>
							<WhiteText className="header-white-text">{localized('matingBatch')}</WhiteText>
						</ViewWeb>
					</ViewWeb>
				)}
			</ViewWeb>
		</PageContainer>
	);
});
